import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { Card, Empty } from 'antd';

import { SyntheticBondChart } from 'state';

export type BondChartMode = 'price' | 'yield';

type Props = {
  bond: SyntheticBondChart;
  mode: BondChartMode;
};

type ChartValue = number | null | undefined;

const tooltipValueFormatter = (value: ChartValue, mode: BondChartMode) => {
  if (typeof value !== 'number') return '';

  const formattedValue = value.toLocaleString(undefined, {
    maximumFractionDigits: 4,
  });
  return mode === 'yield' ? `${formattedValue}%` : formattedValue;
};

const axisValueFormatter = (value: ChartValue, mode: BondChartMode) => {
  if (typeof value !== 'number') return '';
  return mode === 'yield' ? `${value.toFixed(2)}%` : value.toFixed(2);
};

const BondChartCard: React.FC<Props> = ({ bond, mode }) => {
  const series = useMemo(() => {
    const fields =
      mode === 'price'
        ? (['bid', 'ask'] as const)
        : (['bid_yield', 'ask_yield'] as const);

    return fields.map(field => ({
      name: field.replace('_', ' '),
      data: bond.points
        .filter(point => typeof point[field] === 'number')
        .map(point => ({ x: point.timestamp, y: point[field] as number })),
    }));
  }, [bond.points, mode]);

  const hasData = series.some(item => item.data.length > 0);
  const options: any = useMemo(
    () => ({
      chart: {
        id: `bonds-playground-reference-${bond.id}`,
        animations: { enabled: false },
        parentHeightOffset: 0,
        selection: { enabled: true, type: 'x' },
        toolbar: {
          show: true,
          autoSelected: 'zoom',
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
        },
      },
      colors: ['#1677ff', '#fa8c16'],
      dataLabels: { enabled: false },
      grid: { padding: { top: -8, right: 8, bottom: -4, left: 4 } },
      legend: {
        fontSize: '11px',
        horizontalAlign: 'left',
        offsetY: -4,
        position: 'top',
      },
      stroke: { curve: 'straight', width: 1.7 },
      tooltip: {
        shared: true,
        hideEmptySeries: true,
        x: { format: 'dd MMM yyyy HH:mm' },
        y: {
          formatter: (value: ChartValue) => tooltipValueFormatter(value, mode),
        },
      },
      xaxis: {
        type: 'datetime',
        labels: { datetimeUTC: false, style: { fontSize: '10px' } },
        tooltip: { enabled: false },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          minWidth: 42,
          style: { fontSize: '10px' },
          formatter: (value: ChartValue) => axisValueFormatter(value, mode),
        },
      },
    }),
    [bond.id, mode],
  );

  const title =
    bond.ric && bond.ric !== bond.name
      ? `${bond.name} · ${bond.ric}`
      : bond.name;

  return (
    <Card className="bonds-playground__chart-card" size="small" title={title}>
      {hasData ? (
        <Chart options={options} series={series} type="line" height={190} />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`No ${mode} data`}
        />
      )}
    </Card>
  );
};

export default BondChartCard;
